import { z } from "zod";

import { OpaqueIdSchema, UsernameSchema } from "./identity-schemas";
import {
  RelativeAppPathSchema,
  WorkspaceSubdomainSchema,
  WorkspaceSubdomainUpdateSchema,
} from "./schemas";
import { DirectoryEmailSchema } from "./workspace-user-schemas";

export const OpsPermissionSchema = z.enum([
  "audit.read",
  "artifact.read",
  "artifact.upload",
  "billing.collect",
  "billing.manage",
  "billing.read",
  "context.read",
  "context.search",
  "context.write",
  "customer.address.manage",
  "customer.create",
  "customer.provision",
  "customer.read",
  "customer.retry",
  "docs.manage",
  "docs.read",
  "docs.write",
  "organization.read",
  "permissions.manage",
  "support.automation.manage",
  "support.session.start",
  "support.session.write",
  "support.ticket.read",
  "support.ticket.write",
]);

export type OpsPermission = z.infer<typeof OpsPermissionSchema>;

export const OpsRoleKeySchema = z
  .string()
  .min(2)
  .max(80)
  .regex(/^[a-z][a-z0-9_-]*$/);

export const OpsLoginPurposeSchema = z.enum(["shell", "ops"]);

export const OpsOriginSchema = z
  .url()
  .max(253)
  .refine((value) => {
    const url = new URL(value);
    return (
      url.protocol === "https:" &&
      url.port === "" &&
      url.username === "" &&
      url.password === "" &&
      url.pathname === "/" &&
      url.search === "" &&
      url.hash === "" &&
      ["ops.das.dev", "ops-staging.das.dev"].includes(url.hostname)
    );
  }, "Expected an exact DAS operations origin");

export const OpsTransitionExchangeSchema = z.strictObject({
  grant: z.string().min(32).max(256),
});

export const OpsEmployeeSchema = z.strictObject({
  id: OpaqueIdSchema,
  userId: OpaqueIdSchema,
  username: UsernameSchema,
  displayName: z.string().min(1).max(120),
  permissions: z.array(OpsPermissionSchema).max(100),
});

export const OpsMeResponseSchema = z.strictObject({ employee: OpsEmployeeSchema });

export const OPS_MCP_SCOPES = ["ops:read", "ops:search", "ops:write"] as const;
export const OpsMcpScopeSchema = z.enum(OPS_MCP_SCOPES);
export type OpsMcpScope = z.infer<typeof OpsMcpScopeSchema>;

export const OpsMcpKeyExpirationDaysSchema = z.union([z.literal(7), z.literal(30), z.literal(90)]);
export type OpsMcpKeyExpirationDays = z.infer<typeof OpsMcpKeyExpirationDaysSchema>;

export const OpsMcpKeyCreateSchema = z.strictObject({
  label: z.string().trim().min(1).max(80),
  organizationId: OpaqueIdSchema,
  scopes: z
    .array(OpsMcpScopeSchema)
    .min(1)
    .max(OPS_MCP_SCOPES.length)
    .refine((values) => new Set(values).size === values.length, "Scopes must be unique."),
  expiresInDays: OpsMcpKeyExpirationDaysSchema,
});
export type OpsMcpKeyCreate = z.infer<typeof OpsMcpKeyCreateSchema>;

export const OpsMcpKeyStatusSchema = z.enum(["active", "expired", "revoked"]);
export const OpsMcpKeySchema = z.strictObject({
  id: OpaqueIdSchema,
  label: z.string().min(1).max(80),
  organizationId: OpaqueIdSchema,
  customerName: z.string().min(1).max(160),
  scopes: z.array(OpsMcpScopeSchema).min(1).max(OPS_MCP_SCOPES.length),
  status: OpsMcpKeyStatusSchema,
  createdAt: z.number().int().positive(),
  expiresAt: z.number().int().positive(),
  lastUsedAt: z.number().int().positive().nullable(),
  revokedAt: z.number().int().positive().nullable(),
});
export type OpsMcpKey = z.infer<typeof OpsMcpKeySchema>;

export const OpsMcpKeyCustomerSchema = z.strictObject({
  id: OpaqueIdSchema,
  name: z.string().min(1).max(160),
  workspaceUrl: z.url(),
  allowedScopes: z.array(OpsMcpScopeSchema).max(OPS_MCP_SCOPES.length),
});
export type OpsMcpKeyCustomer = z.infer<typeof OpsMcpKeyCustomerSchema>;

export const OpsMcpKeysResponseSchema = z.strictObject({
  keys: z.array(OpsMcpKeySchema).max(100),
  customers: z.array(OpsMcpKeyCustomerSchema).max(500),
});

export const OpsMcpKeyCreateResponseSchema = z.strictObject({
  key: OpsMcpKeySchema,
  token: z.string().regex(/^das_mcp_[A-Za-z0-9_-]{43}$/),
});

export const OpsMcpKeyRevokeResponseSchema = z.strictObject({ revoked: z.literal(true) });

export const OpsEmployeeStatusSchema = z.enum(["active", "disabled"]);
export type OpsEmployeeStatus = z.infer<typeof OpsEmployeeStatusSchema>;

export const OpsRoleBundleSchema = z.strictObject({
  key: OpsRoleKeySchema,
  displayName: z.string().min(1).max(120),
  description: z.string().min(3).max(300),
  status: OpsEmployeeStatusSchema,
  permissions: z.array(OpsPermissionSchema).max(100),
});
export type OpsRoleBundle = z.infer<typeof OpsRoleBundleSchema>;

export const OpsEmployeeRoleAssignmentSchema = z.strictObject({
  roleKey: OpsRoleKeySchema,
  organizationId: OpaqueIdSchema.nullable(),
  organizationName: z.string().min(1).max(160).nullable(),
  expiresAt: z.number().int().positive().nullable(),
});

export const OpsEmployeeDirectoryItemSchema = z.strictObject({
  id: OpaqueIdSchema,
  userId: OpaqueIdSchema,
  username: UsernameSchema,
  displayName: z.string().min(1).max(120),
  status: OpsEmployeeStatusSchema,
  globalRoleKeys: z.array(OpsRoleKeySchema).max(100),
  roleAssignments: z.array(OpsEmployeeRoleAssignmentSchema).max(200),
  permissions: z.array(OpsPermissionSchema).max(100),
  updatedAt: z.number().int().positive(),
});
export type OpsEmployeeDirectoryItem = z.infer<typeof OpsEmployeeDirectoryItemSchema>;

export const OpsEmployeePageSizeSchema = z.union([z.literal(10), z.literal(25), z.literal(50)]);
export type OpsEmployeePageSize = z.infer<typeof OpsEmployeePageSizeSchema>;

export const OpsEmployeesQuerySchema = z.strictObject({
  q: z.string().trim().min(1).max(100).optional(),
  page: z.coerce.number().int().min(1).max(100_000).default(1),
  pageSize: z.coerce.number().pipe(OpsEmployeePageSizeSchema).default(25),
});

export const OpsEmployeesResponseSchema = z.strictObject({
  employees: z.array(OpsEmployeeDirectoryItemSchema).max(50),
  roleBundles: z.array(OpsRoleBundleSchema).max(100),
  page: z.number().int().positive(),
  pageSize: OpsEmployeePageSizeSchema,
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().positive(),
});

export const OpsEmployeeStatusUpdateSchema = z.strictObject({
  status: OpsEmployeeStatusSchema,
});

export const OpsEmployeeRolesUpdateSchema = z.strictObject({
  roleKeys: z
    .array(OpsRoleKeySchema)
    .max(20)
    .refine((keys) => new Set(keys).size === keys.length, "Role keys must be unique."),
});

/**
 * Grant operations access to somebody who already has a platform account.
 *
 * Deliberately keyed on the verified sign-in address rather than an opaque user id: an
 * administrator adding a colleague knows their address, not their `usr_` identifier. Creating the
 * underlying account stays outside this surface — operations access is granted to a person the
 * platform already knows, never used to conjure one.
 */
export const OpsEmployeeCreateSchema = z.strictObject({
  email: DirectoryEmailSchema,
  roleKeys: z
    .array(OpsRoleKeySchema)
    .min(1)
    .max(20)
    .refine((keys) => new Set(keys).size === keys.length, "Role keys must be unique."),
});
export type OpsEmployeeCreate = z.infer<typeof OpsEmployeeCreateSchema>;

export const OpsEmployeeCustomerRolesUpdateSchema = OpsEmployeeRolesUpdateSchema.extend({
  organizationId: OpaqueIdSchema,
});

export const OpsEmployeeCustomerRolesMutationResponseSchema = z.strictObject({
  employeeId: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  roleKeys: z.array(OpsRoleKeySchema).max(100),
  updatedAt: z.number().int().positive(),
});

export const OpsEmployeeMutationResponseSchema = z.strictObject({
  employeeId: OpaqueIdSchema,
  status: OpsEmployeeStatusSchema,
  globalRoleKeys: z.array(OpsRoleKeySchema).max(100),
  updatedAt: z.number().int().positive(),
});

export const CustomerProvisioningStatusSchema = z.enum([
  "requested",
  "provisioning",
  "verifying",
  "ready",
  "failed",
  "degraded",
  "retiring",
]);

export const OpsCustomerCreateSchema = z.strictObject({
  name: z.string().trim().min(2).max(160),
  subdomain: WorkspaceSubdomainSchema,
  firstAdmin: z
    .strictObject({
      displayName: z.string().trim().min(2).max(120),
      email: DirectoryEmailSchema,
    })
    .optional(),
});
export type OpsCustomerCreate = z.infer<typeof OpsCustomerCreateSchema>;

export const OpsCustomerSummarySchema = z.strictObject({
  id: OpaqueIdSchema,
  slug: z
    .string()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9-]+$/),
  name: z.string().min(2).max(160),
  subdomain: WorkspaceSubdomainSchema,
  status: z.enum(["active", "suspended", "closed"]),
  provisioningStatus: CustomerProvisioningStatusSchema,
  workspaceUrl: z.url(),
  createdAt: z.number().int().positive(),
});
export type OpsCustomerSummary = z.infer<typeof OpsCustomerSummarySchema>;

export const OpsCustomerSlugSchema = OpsCustomerSummarySchema.shape.slug;
export const OpsCustomerPageSizeSchema = z.union([z.literal(10), z.literal(25), z.literal(50)]);
export type OpsCustomerPageSize = z.infer<typeof OpsCustomerPageSizeSchema>;

export const OpsCustomersQuerySchema = z.strictObject({
  q: z.string().trim().min(1).max(100).optional(),
  page: z.coerce.number().int().min(1).max(100_000).default(1),
  pageSize: z.coerce.number().pipe(OpsCustomerPageSizeSchema).default(25),
});

export const OpsCustomersResponseSchema = z.strictObject({
  customers: z.array(OpsCustomerSummarySchema).max(50),
  page: z.number().int().positive(),
  pageSize: OpsCustomerPageSizeSchema,
  total: z.number().int().nonnegative(),
  totalPages: z.number().int().positive(),
});

export const OpsCustomerResponseSchema = z.strictObject({ customer: OpsCustomerSummarySchema });

export const OpsWorkspaceAddressUpdateSchema = WorkspaceSubdomainUpdateSchema;
export type OpsWorkspaceAddressUpdate = z.infer<typeof OpsWorkspaceAddressUpdateSchema>;

export const OpsCustomerCreateResponseSchema = z.strictObject({
  customer: OpsCustomerSummarySchema,
  provisioningRunId: OpaqueIdSchema,
  invitationId: OpaqueIdSchema.nullable(),
  replayed: z.boolean(),
});

export const OpsSupportSessionCreateSchema = z
  .strictObject({
    reason: z.string().trim().min(10).max(500),
    caseId: OpaqueIdSchema.optional(),
    targetUserId: OpaqueIdSchema.optional(),
    mode: z.enum(["read", "write"]).default("read"),
    confirmWrite: z.boolean().default(false),
  })
  .superRefine((value, context) => {
    if (value.mode === "write" && !value.confirmWrite) {
      context.addIssue({
        code: "custom",
        path: ["confirmWrite"],
        message: "Write support access requires explicit confirmation.",
      });
    }
  });

export const OpsSupportSessionSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  targetUserId: OpaqueIdSchema.nullable(),
  caseId: OpaqueIdSchema.nullable(),
  mode: z.enum(["read", "write"]),
  reason: z.string().min(10).max(500),
  expiresAt: z.number().int().positive(),
  /**
   * The customer approval this session was opened under, or null for the unilateral ADR-0062 path.
   *
   * Surfaced so the console can show a session beneath the approval that permitted it. Rendering an
   * approved session under a heading that says it was not approved is the label contradicting the
   * fact, which on a consent surface is worse than showing nothing.
   */
  accessRequestId: OpaqueIdSchema.nullable(),
});
export type OpsSupportSession = z.infer<typeof OpsSupportSessionSchema>;
export const OpsSupportSessionResponseSchema = z.strictObject({
  supportSession: OpsSupportSessionSchema,
});
export const OpsSupportSessionsResponseSchema = z.strictObject({
  supportSessions: z.array(OpsSupportSessionSchema).max(20),
});
export const OpsSupportSessionRevokeResponseSchema = z.strictObject({
  revoked: z.literal(true),
});

export const OpsSupportTargetUserSchema = z.strictObject({
  id: OpaqueIdSchema,
  displayName: z.string().min(1).max(120),
  username: UsernameSchema,
  role: z.enum(["platform_admin", "organization_admin", "member"]),
});
export const OpsSupportTargetToolSchema = z.strictObject({
  id: OpaqueIdSchema,
  displayName: z.string().min(1).max(120),
});
export type OpsSupportTargetTool = z.infer<typeof OpsSupportTargetToolSchema>;
export const OpsSupportTargetsResponseSchema = z.strictObject({
  users: z.array(OpsSupportTargetUserSchema).max(500),
  tools: z.array(OpsSupportTargetToolSchema).max(500),
});

export const OpsSupportLaunchCreateSchema = z.strictObject({
  appInstanceId: OpaqueIdSchema,
  intendedPath: RelativeAppPathSchema.default("/"),
});
export type OpsSupportLaunchCreate = z.infer<typeof OpsSupportLaunchCreateSchema>;
export const OpsSupportLaunchResponseSchema = z.strictObject({
  appInstanceId: OpaqueIdSchema,
  technicalOrigin: z.url(),
  grant: z.string().min(32).max(256),
  expiresAt: z.number().int().positive(),
});
export type OpsSupportLaunchResponse = z.infer<typeof OpsSupportLaunchResponseSchema>;

export const CustomerProvisioningStepKeySchema = z.enum([
  "context-d1",
  "context-r2",
  "ai-search",
  "context-worker",
  "verify",
]);
export type CustomerProvisioningStepKey = z.infer<typeof CustomerProvisioningStepKeySchema>;

export const CustomerProvisioningWorkflowParamsSchema = z.strictObject({
  version: z.literal(1),
  organizationId: OpaqueIdSchema,
  provisioningRunId: OpaqueIdSchema,
});
export type CustomerProvisioningWorkflowParams = z.infer<
  typeof CustomerProvisioningWorkflowParamsSchema
>;

const ProvisioningResultBaseSchema = z.strictObject({
  step: CustomerProvisioningStepKeySchema,
  replayed: z.boolean(),
});

export const CustomerProvisioningResultSchema = z.discriminatedUnion("step", [
  ProvisioningResultBaseSchema.extend({
    step: z.literal("context-d1"),
    d1DatabaseName: z.string().min(3).max(64),
    d1DatabaseId: z.uuid(),
    schemaVersion: z.number().int().positive(),
  }),
  ProvisioningResultBaseSchema.extend({
    step: z.literal("context-r2"),
    r2BucketName: z.string().min(3).max(63),
  }),
  ProvisioningResultBaseSchema.extend({
    step: z.literal("ai-search"),
    aiSearchInstance: z.string().min(1).max(64),
  }),
  ProvisioningResultBaseSchema.extend({
    step: z.literal("context-worker"),
    workerName: z.string().min(3).max(64),
    releaseSha256: z
      .string()
      .length(64)
      .regex(/^[0-9a-f]+$/),
  }),
  ProvisioningResultBaseSchema.extend({
    step: z.literal("verify"),
    workerName: z.string().min(3).max(64),
    d1DatabaseName: z.string().min(3).max(64),
    d1DatabaseId: z.uuid(),
    r2BucketName: z.string().min(3).max(63),
    aiSearchInstance: z.string().min(1).max(64),
    schemaVersion: z.number().int().positive(),
  }),
]);
export type CustomerProvisioningResult = z.infer<typeof CustomerProvisioningResultSchema>;

export const OpsProvisioningStepSchema = z.strictObject({
  key: CustomerProvisioningStepKeySchema,
  status: z.enum(["pending", "running", "complete", "failed"]),
  attempt: z.number().int().nonnegative(),
  startedAt: z.number().int().positive().nullable(),
  completedAt: z.number().int().positive().nullable(),
  errorCode: z.string().min(1).max(100).nullable(),
});

export const OpsProvisioningRunSchema = z.strictObject({
  id: OpaqueIdSchema,
  organizationId: OpaqueIdSchema,
  customerName: z.string().min(2).max(160),
  status: CustomerProvisioningStatusSchema,
  attempt: z.number().int().positive(),
  createdAt: z.number().int().positive(),
  updatedAt: z.number().int().positive(),
  completedAt: z.number().int().positive().nullable(),
  lastErrorCode: z.string().min(1).max(100).nullable(),
  steps: z.array(OpsProvisioningStepSchema).length(5),
});

export const OpsProvisioningRunsResponseSchema = z.strictObject({
  runs: z.array(OpsProvisioningRunSchema).max(200),
});

export const OpsProvisioningRetryResponseSchema = z.strictObject({
  organizationId: OpaqueIdSchema,
  provisioningRunId: OpaqueIdSchema,
});
